/* Opvolgpunten (ADR 0028): wat uit de eigen mail van een teamlid om een vervolg vraagt, per
 * project. De herkenning gebeurt in NATS; het CRM toont, koppelt en laat afvinken. De client komt
 * als argument binnen, zodat de toetsen hem kunnen nabootsen.
 *
 * Een verstuurd stuk heeft een klok (ADR 0030): NATS levert het soort stuk, de versie, wanneer het
 * wegging en de dag waarop de eigenaar moet namailen of bellen. Het CRM rekent alleen uit hoe lang
 * het stil is en schuift de dag een week op als de eigenaar zelf heeft opgevolgd. Het verstuurt niets. */

export interface OpvolgClient {
  from(tabel: string): any;
}

export type OpvolgSoort = 'toezegging' | 'wacht_op_antwoord' | 'open_vraag';
export type OpvolgStatus = 'open' | 'afgehandeld' | 'vervallen';
export type StukSoort = 'offerte' | 'voorstel' | 'businesscase' | 'analyse' | 'presentatie';
/** Na een eigen opvolging (gebeld of gemaild) is de volgende actie een week later. */
export const HERHAAL_DAGEN = 7;

export interface Opvolgpunt {
  id: string;
  eigenaar_id: string;
  soort: OpvolgSoort;
  tekst: string;
  /** De dag waarop het klaar moet zijn (YYYY-MM-DD), of null als er geen termijn is genoemd. */
  termijn: string | null;
  contact_naam: string | null;
  contact_email: string | null;
  onderwerp: string | null;
  verzonden_op: string | null;
  draad_id: string | null;
  mail_url: string | null;
  status: OpvolgStatus;
  afgehandeld_op: string | null;
  project_id: string | null;
  org_id: string | null;
  koppeling: 'geen' | 'vanzelf' | 'handmatig';
  koppel_reden: string | null;
  /** Het stuk waar dit punt op wacht; leeg bij elk ander punt. */
  stuk_soort: StukSoort | null;
  stuk_versie: string | null;
  stuk_bestand: string | null;
  /** Wanneer het wachten begon: het moment dat het stuk of de vraag wegging. */
  klok_start: string | null;
  /** De dag (YYYY-MM-DD) waarop de eigenaar moet namailen of bellen, door NATS uitgerekend. */
  actie_op: string | null;
  /** De dag waarop de eigenaar zelf heeft gebeld of gemaild. */
  opgevolgd_op: string | null;
  /** Telefoon van het Contact met dit e-mailadres, als het CRM dat kent. */
  telefoon: string | null;
  /** Wie het toezegde; alleen gevuld op de Projectpagina, waar ook collega's meelezen. */
  eigenaar_naam: string | null;
}

/** Een rij zoals de tabel hem geeft; telefoon en naam komen er bij het lezen bij. */
type Rij = Omit<Opvolgpunt, 'telefoon' | 'eigenaar_naam'>;

export interface ProjectOpvolging {
  open: Opvolgpunt[];
  afgehandeld: Opvolgpunt[];
}

const KOLOMMEN =
  'id, eigenaar_id, soort, tekst, termijn, contact_naam, contact_email, onderwerp, verzonden_op, draad_id, mail_url, status, afgehandeld_op, project_id, org_id, koppeling, koppel_reden, stuk_soort, stuk_versie, stuk_bestand, klok_start, actie_op, opgevolgd_op';

export const SOORT_LABEL: Record<OpvolgSoort, string> = {
  toezegging: 'Toegezegd',
  wacht_op_antwoord: 'Wacht op antwoord',
  open_vraag: 'Vraag aan ons',
};

async function rijen<T>(q: PromiseLike<{ data: T[] | null; error: unknown }>): Promise<T[]> {
  try {
    const r = await q;
    return r.error ? [] : r.data ?? [];
  } catch {
    return [];
  }
}

/* ── dagen en volgorde ────────────────────────────────────────────── */

/** YYYY-MM-DD in de tijdzone van de browser; UTC zou 's avonds laat een dag te vroeg 'te laat' melden. */
export const lokaleDag = (d: Date) => new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
const middag = (dag: string) => new Date(`${dag}T12:00:00`).getTime();

/** Hele dagen tot de termijn; negatief is te laat, null is geen termijn. */
export function dagenTot(termijn: string | null, nu: Date = new Date()): number | null {
  return termijn ? Math.round((middag(termijn) - middag(lokaleDag(nu))) / 86_400_000) : null;
}

export function termijnTekst(termijn: string | null, nu: Date = new Date()): string {
  const d = dagenTot(termijn, nu);
  if (d === null) return 'geen termijn';
  if (d < 0) return `${-d} ${d === -1 ? 'dag' : 'dagen'} te laat`;
  if (d === 0) return 'vandaag';
  if (d === 1) return 'morgen';
  return `over ${d} dagen`;
}

/* ── de klok van een verstuurd stuk ───────────────────────────────── */

type Klok = Pick<Opvolgpunt, 'termijn' | 'actie_op' | 'opgevolgd_op'>;

const plusDagen = (dag: string, n: number) => lokaleDag(new Date(middag(dag) + n * 86_400_000));

/** De dag waarop de eigenaar aan de beurt is: de termijn of de dag van de actie, en na een eigen
 *  opvolging een week daarna (de latere van de twee telt). */
export function actieDag(p: Klok): string | null {
  const basis = p.actie_op ?? p.termijn;
  if (!p.opgevolgd_op) return basis;
  const week = plusDagen(p.opgevolgd_op, HERHAAL_DAGEN);
  return basis && basis > week ? basis : week;
}

/** Werkdagen (maandag tot en met vrijdag) sinds het stuk wegging; de verzenddag zelf telt niet. */
export function werkdagenStil(klokStart: string | null, nu: Date = new Date()): number | null {
  if (!klokStart) return null;
  const vandaag = lokaleDag(nu);
  let dag = lokaleDag(new Date(klokStart));
  let n = 0;
  while (dag < vandaag) {
    dag = plusDagen(dag, 1);
    const weekdag = new Date(`${dag}T12:00:00`).getDay();
    if (weekdag !== 0 && weekdag !== 6) n++;
  }
  return n;
}

export function stilTekst(klokStart: string | null, nu: Date = new Date()): string | null {
  const n = werkdagenStil(klokStart, nu);
  if (n === null) return null;
  if (n === 0) return 'vandaag verstuurd';
  return `${n} ${n === 1 ? 'werkdag' : 'werkdagen'} stil`;
}

const STUK_LABEL: Record<StukSoort, string> = {
  offerte: 'Offerte', voorstel: 'Voorstel', businesscase: 'Businesscase', analyse: 'Analyse', presentatie: 'Presentatie',
};

/** "Voorstel v3.1", of null als het punt niet over een stuk gaat. */
export const stukLabel = (p: Pick<Opvolgpunt, 'stuk_soort' | 'stuk_versie'>) =>
  p.stuk_soort ? `${STUK_LABEL[p.stuk_soort]}${p.stuk_versie ? ` v${p.stuk_versie}` : ''}` : null;

/** Wat de eigenaar wanneer moet doen. Een herinnering aan hemzelf: het CRM verstuurt niets. */
export function actieTekst(p: Klok, nu: Date = new Date()): string {
  const d = dagenTot(actieDag(p), nu);
  if (d === null) return 'geen termijn';
  if (d < 0) return `${-d} ${d === -1 ? 'dag' : 'dagen'} te laat: namailen of bellen`;
  if (d === 0) return 'vandaag namailen of bellen';
  if (d === 1) return 'morgen namailen of bellen';
  return `over ${d} dagen namailen of bellen`;
}

/** Te laat eerst (het oudste bovenaan), dan op de dag van de actie, en zonder dag onderaan met de nieuwste mail eerst. */
export function sorteer<T extends Klok & Pick<Opvolgpunt, 'verzonden_op'>>(punten: T[]): T[] {
  return [...punten].sort((a, b) => {
    const [da, db] = [actieDag(a), actieDag(b)];
    if (da && db && da !== db) return da.localeCompare(db);
    if (da && !db) return -1;
    if (db && !da) return 1;
    return (b.verzonden_op ?? '').localeCompare(a.verzonden_op ?? '');
  });
}

export interface OpvolgGroepen<T> {
  /** De bal ligt bij de relatie: een verstuurd stuk of een vraag van ons. */
  wachtOpReactie: T[];
  /** Toezeggingen van ons en vragen van relaties. */
  ikBenAanZet: T[];
}

/** De twee groepen van het dashboard, elk gesorteerd met te laat bovenaan. */
export function groepeer<T extends Klok & Pick<Opvolgpunt, 'soort' | 'verzonden_op'>>(punten: T[]): OpvolgGroepen<T> {
  const gesorteerd = sorteer(punten);
  return {
    wachtOpReactie: gesorteerd.filter((p) => p.soort === 'wacht_op_antwoord'),
    ikBenAanZet: gesorteerd.filter((p) => p.soort !== 'wacht_op_antwoord'),
  };
}

/** Eén project op het dashboard: de kop boven zijn punten. */
export interface ProjectKop {
  nummer: string | null;
  naam: string;
  stage: string | null;
}

export interface ProjectGroep<T> {
  /** null is "zonder project"; die groep staat altijd onderaan. */
  projectId: string | null;
  punten: T[];
}

/** De punten per project, in de volgorde van hun meest dringende punt (de lijst is al gesorteerd,
 *  dus de eerste van een groep bepaalt de plek). Zonder project komt onderaan. */
export function perProject<T extends Pick<Opvolgpunt, 'project_id'>>(gesorteerd: T[]): ProjectGroep<T>[] {
  const groepen = new Map<string | null, T[]>();
  for (const p of gesorteerd) {
    const lijst = groepen.get(p.project_id) ?? [];
    lijst.push(p);
    groepen.set(p.project_id, lijst);
  }
  const zonder = groepen.get(null);
  groepen.delete(null);
  const met = [...groepen].map(([projectId, punten]) => ({ projectId, punten }));
  return zonder ? [...met, { projectId: null, punten: zonder }] : met;
}

/** De dag die voor het punt telt: de actiedag van een klok, of de termijn. */
export const puntDag = (p: Klok & Pick<Opvolgpunt, 'soort'>) => (p.soort === 'wacht_op_antwoord' ? actieDag(p) : p.termijn);

/** De dag waarop het punt wegging, als korte datum ('2 okt'), of null. */
export function verstuurdDag(p: Pick<Opvolgpunt, 'klok_start' | 'verzonden_op'>): string | null {
  const iso = p.klok_start ?? p.verzonden_op;
  if (!iso) return null;
  const d = new Date(iso.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? null : new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short' }).format(d).replace('.', '');
}

/* ── lezen ────────────────────────────────────────────────────────── */

async function metTelefoon<T extends { contact_email: string | null }>(client: OpvolgClient, punten: T[]): Promise<(T & { telefoon: string | null })[]> {
  const adressen = [...new Set(punten.map((p) => p.contact_email).filter((e): e is string => Boolean(e)))];
  const contacten = adressen.length
    ? await rijen<{ email: string | null; phone: string | null; mobile: string | null }>(
        client.from('contacts').select('email, phone, mobile').in('email', adressen).is('deleted_at', null),
      )
    : [];
  const nummer = new Map(contacten.map((c) => [(c.email ?? '').toLowerCase(), c.mobile || c.phone || null]));
  return punten.map((p) => ({ ...p, telefoon: nummer.get((p.contact_email ?? '').toLowerCase()) ?? null }));
}

/** Mijn open punten, ook die nog aan geen project hangen. */
export async function haalMijnOpvolging(client: OpvolgClient, userId: string): Promise<Opvolgpunt[]> {
  const lijst = await rijen<Rij>(
    client.from('opvolgpunten').select(KOLOMMEN).eq('eigenaar_id', userId).eq('status', 'open').limit(100),
  );
  const metNummer = await metTelefoon(client, lijst);
  return sorteer(metNummer.map((p) => ({ ...p, eigenaar_naam: null })));
}

/** Alles van één project: wat openstaat, en wat de laatste 30 dagen is afgehandeld. */
export async function haalOpvolgingVanProject(client: OpvolgClient, projectId: string, nu: Date = new Date()): Promise<ProjectOpvolging> {
  const sinds = new Date(nu.getTime() - 30 * 86_400_000).toISOString();
  const [open, dicht] = await Promise.all([
    rijen<Rij>(client.from('opvolgpunten').select(KOLOMMEN).eq('project_id', projectId).eq('status', 'open').limit(100)),
    rijen<Rij>(
      client.from('opvolgpunten').select(KOLOMMEN).eq('project_id', projectId).eq('status', 'afgehandeld')
        .gte('afgehandeld_op', sinds).order('afgehandeld_op', { ascending: false }).limit(20),
    ),
  ]);
  const ids = [...new Set([...open, ...dicht].map((p) => p.eigenaar_id))];
  const mensen = ids.length ? await rijen<{ id: string; display_name: string | null; email: string | null }>(client.from('profiles').select('id, display_name, email').in('id', ids)) : [];
  const naam = new Map(mensen.map((m) => [m.id, m.display_name || m.email?.split('@')[0] || null]));
  const af = async (l: Rij[]) => (await metTelefoon(client, l)).map((p) => ({ ...p, eigenaar_naam: naam.get(p.eigenaar_id) ?? null }));
  return { open: sorteer(await af(open)), afgehandeld: await af(dicht) };
}

/** Het aantal voor de sectiebalk van de Projectpagina, en of het blok er hoort te staan. */
export const aantalOpvolging = (o: ProjectOpvolging | undefined) => (o ? o.open.length : 0);
export const heeftOpvolging = (o: ProjectOpvolging | undefined) => Boolean(o && (o.open.length || o.afgehandeld.length));

/** Voor de tegel op het dashboard: hoeveel van mij staan open, en hoeveel zijn over de dag van de actie.
 *  Geteld in de browser: of iets te laat is hangt ook af van wanneer de eigenaar zelf heeft opgevolgd. */
export async function telMijnOpvolging(client: OpvolgClient, userId: string, nu: Date = new Date()): Promise<{ open: number; teLaat: number }> {
  const lijst = await rijen<Klok>(
    client.from('opvolgpunten').select('termijn, actie_op, opgevolgd_op').eq('eigenaar_id', userId).eq('status', 'open').limit(500),
  );
  const vandaag = lokaleDag(nu);
  return { open: lijst.length, teLaat: lijst.filter((p) => (actieDag(p) ?? vandaag) < vandaag).length };
}

/* ── verstuurde stukken per project ───────────────────────────────── */

export interface Verstuurd {
  /** Aan wie: de naam van het contact, anders het adres. */
  aan: string | null;
  /** Wanneer het wegging. */
  op: string;
}

/** Per bestandsnaam (kleine letters) wanneer en aan wie hij het laatst is verstuurd, voor de
 *  versietijdlijn op de Projectpagina. Ook van punten die al zijn afgehandeld: verstuurd blijft verstuurd. */
export async function haalVerstuurdeStukken(client: OpvolgClient, projectId: string): Promise<Map<string, Verstuurd>> {
  const lijst = await rijen<Pick<Opvolgpunt, 'stuk_bestand' | 'contact_naam' | 'contact_email' | 'klok_start' | 'verzonden_op'>>(
    client.from('opvolgpunten').select('stuk_bestand, contact_naam, contact_email, klok_start, verzonden_op')
      .eq('project_id', projectId).not('stuk_bestand', 'is', null).limit(200),
  );
  const uit = new Map<string, Verstuurd>();
  for (const p of lijst) {
    const op = p.klok_start ?? p.verzonden_op;
    if (!p.stuk_bestand || !op) continue;
    const naam = p.stuk_bestand.normalize('NFC').trim().toLowerCase();
    const al = uit.get(naam);
    if (!al || al.op < op) uit.set(naam, { aan: p.contact_naam || p.contact_email, op });
  }
  return uit;
}

/** "verstuurd aan Kayleigh op 21 sep, 9 dagen geleden". */
export function verstuurdTekst(v: Verstuurd, nu: Date = new Date()): string {
  const dag = lokaleDag(new Date(v.op));
  const geleden = -(dagenTot(dag, nu) ?? 0);
  const op = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short' }).format(new Date(`${dag}T12:00:00`)).replace('.', '');
  const hoeLang = geleden <= 0 ? 'vandaag' : geleden === 1 ? 'gisteren' : `${geleden} dagen geleden`;
  return `verstuurd${v.aan ? ` aan ${v.aan}` : ''} op ${op}, ${hoeLang}`;
}

/* ── schrijven (alleen de eigenaar; RLS weigert de rest) ──────────── */

export async function zetOpvolgStatus(client: OpvolgClient, id: string, status: OpvolgStatus): Promise<void> {
  const { error } = await client.from('opvolgpunten').update({ status }).eq('id', id);
  if (error) throw error;
}

/** De eigenaar heeft zelf gebeld of gemaild: de klok loopt een week opnieuw. Legt alleen de dag vast;
 *  het CRM verstuurt niets. `null` haalt het weer weg. */
export async function zetOpgevolgd(client: OpvolgClient, id: string, dag: string | null): Promise<void> {
  const { error } = await client.from('opvolgpunten').update({ opgevolgd_op: dag }).eq('id', id);
  if (error) throw error;
}

/** Koppelt het punt aan een project, en de andere nog niet gekoppelde punten uit dezelfde draad ook. */
export async function koppelAanProject(client: OpvolgClient, punt: Pick<Opvolgpunt, 'id' | 'eigenaar_id' | 'draad_id'>, projectId: string | null): Promise<void> {
  const { error } = await client.from('opvolgpunten').update({ project_id: projectId }).eq('id', punt.id);
  if (error) throw error;
  if (!projectId || !punt.draad_id) return;
  const draad = await client.from('opvolgpunten').update({ project_id: projectId })
    .eq('eigenaar_id', punt.eigenaar_id).eq('draad_id', punt.draad_id).is('project_id', null);
  if (draad.error) throw draad.error;
}
